/**
 * About Us Page
 *
 * Company information and mission statement with SEO optimization.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, useStructuredData, SITE_CONFIG } from '../../lib/seo';
import PublicLayout from '../../components/layout/PublicLayout';

export default function AboutPage() {
  const { i18n } = useTranslation();

  useSEO({
    title: 'About xtFORM | Our Mission to Simplify Data Collection',
    description: 'Learn about xtFORM, a free form builder platform dedicated to making data collection simple, secure, and accessible for everyone. Our story, mission, and values.',
    keywords: 'about xtFORM, form builder company, our mission, free forms platform, data collection platform',
    url: `${SITE_CONFIG.domain}/about`,
    locale: i18n.language,
  });

  useHreflang('/about');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'xtFORM',
    'url': SITE_CONFIG.domain,
    'description': 'Free form builder platform for creating forms, surveys, and live quizzes.',
    'foundingDate': '2024',
    'sameAs': [],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer support',
      'email': 'team@xtform.com',
    },
  };

  useStructuredData(structuredData, 'about-organization');

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About xtFORM
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We believe that collecting data should be simple, secure, and free.
            That's why we built xtFORM.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-gray-600 text-lg mb-6">
              In a world where data collection tools often come with complex pricing, limited features, or privacy concerns,
              we set out to create something different. xtFORM (pronounced "ekstam") was born from a simple idea:
              <strong className="text-gray-900"> everyone deserves access to powerful form-building tools, regardless of budget.</strong>
            </p>
            <p className="text-gray-600 text-lg mb-6">
              Whether you're a teacher creating quizzes for your classroom, a small business collecting customer feedback,
              or an event organizer managing registrations - you shouldn't have to pay premium prices for basic functionality.
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Beautiful Forms</h3>
              <p className="text-gray-600">
                Drag-and-drop builder with 50+ field types, conditional logic, and customizable themes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Surveys & Polls</h3>
              <p className="text-gray-600">
                Collect feedback with rating scales, NPS scores, and real-time results visualization.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Live Quizzes</h3>
              <p className="text-gray-600">
                Interactive game-show style quizzes with real-time leaderboards, perfect for education and events.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Powerful API</h3>
              <p className="text-gray-600">
                RESTful API for developers to integrate forms into their applications and automate workflows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Values</h2>
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl">1</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Accessibility First</h3>
                <p className="text-gray-600">
                  We believe great tools should be available to everyone. Our core features are completely free,
                  with no artificial limitations or premium gates on essential functionality.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl">2</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Privacy & Security</h3>
                <p className="text-gray-600">
                  Your data belongs to you. We use AES-256 encryption, never sell user data, and are fully GDPR compliant.
                  We're transparent about our practices and committed to protecting your information.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl">3</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Simplicity</h3>
                <p className="text-gray-600">
                  Powerful doesn't have to mean complicated. We design our tools to be intuitive so you can
                  focus on what matters - collecting the data you need without a learning curve.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl">4</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Continuous Improvement</h3>
                <p className="text-gray-600">
                  We're always listening to user feedback and improving our platform. Your suggestions directly
                  influence our roadmap as we work to build the best form builder possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Built on Modern Technology</h2>
          <p className="text-gray-600 text-lg text-center mb-8 max-w-2xl mx-auto">
            We leverage cutting-edge technology to deliver a fast, reliable, and scalable platform.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl mb-2">⚡</div>
              <div className="font-medium text-gray-900">Cloudflare</div>
              <div className="text-sm text-gray-500">Edge Network</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl mb-2">🔒</div>
              <div className="font-medium text-gray-900">AES-256</div>
              <div className="text-sm text-gray-500">Encryption</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl mb-2">🌍</div>
              <div className="font-medium text-gray-900">Global CDN</div>
              <div className="text-sm text-gray-500">Fast Everywhere</div>
            </div>
            <div className="bg-white rounded-lg p-4 text-center">
              <div className="text-2xl mb-2">🔌</div>
              <div className="font-medium text-gray-900">REST API</div>
              <div className="text-sm text-gray-500">Integrations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
          <p className="text-gray-600 text-lg mb-6">
            Have questions, feedback, or just want to say hello? We'd love to hear from you.
          </p>
          <a
            href="mailto:team@xtform.com"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-lg font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            team@xtform.com
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Join thousands of users who trust xtFORM for their form and quiz needs.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-colors"
          >
            Create Free Account
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
